import {
    type ElementDwellSpansCache,
    endElementDwellSpan,
    startElementDwellSpan
} from "../state/element-dwell-times";

import type { ElementTrackability } from "./trackability";
import type { Clock } from "./types";

const FALLBACK_INTERSECTION_THRESHOLD = 0.5;

// Discover trackable elements (per `trackable.selector`) and watch their
// viewport intersection. Mutates the supplied span cache as elements
// enter/exit view, and reacts to DOM mutations (added/removed nodes,
// visibility changes).
export class DomObserver {
    private started = false;
    private observedElements: WeakSet<Element> | undefined;
    private observersByThreshold: Map<number, IntersectionObserver> | undefined;
    private mutationObserver: MutationObserver | undefined;
    private readonly cache: ElementDwellSpansCache;
    private readonly trackable: ElementTrackability;
    private readonly clock: Clock;

    public constructor(
        cache: ElementDwellSpansCache,
        trackable: ElementTrackability,
        clock: Clock
    ) {
        this.cache = cache;
        this.trackable = trackable;
        this.clock = clock;
    }

    public start = () => {
        if (this.started) {
            return;
        }
        if (
            typeof document === "undefined" ||
            document.body === null ||
            typeof IntersectionObserver === "undefined"
        ) {
            this.started = true;
            return;
        }
        this.started = true;

        this.observedElements = new WeakSet<Element>();
        this.observersByThreshold = new Map<number, IntersectionObserver>();

        const getOrCreateObserver = (
            threshold: number
        ): IntersectionObserver => {
            const existing = this.observersByThreshold?.get(threshold);
            if (existing !== undefined) {
                return existing;
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    entries.forEach((entry) => {
                        const elementKey = this.trackable.getKey(entry.target);
                        if (!elementKey) {
                            return;
                        }

                        const inView =
                            entry.isIntersecting &&
                            entry.intersectionRatio >= threshold &&
                            isElementVisuallyPresent(entry.target);

                        if (inView) {
                            startElementDwellSpan(
                                this.cache,
                                elementKey,
                                this.clock()
                            );
                        } else {
                            endElementDwellSpan(
                                this.cache,
                                elementKey,
                                this.clock()
                            );
                        }
                    });
                },
                { threshold }
            );

            // Guard against a race where stop() ran between constructing the
            // observer and registering it. If the map is gone, disconnect
            // immediately so the observer doesn't leak.
            if (this.observersByThreshold === undefined) {
                observer.disconnect();
                return observer;
            }
            this.observersByThreshold.set(threshold, observer);
            return observer;
        };

        const observeElement = (el: Element) => {
            if (this.observedElements?.has(el)) {
                return;
            }
            this.observedElements?.add(el);
            getOrCreateObserver(resolveThreshold(this.trackable, el)).observe(
                el
            );
        };

        const scanAndObserve = (root: ParentNode) => {
            root.querySelectorAll(this.trackable.selector).forEach(
                observeElement
            );
        };

        scanAndObserve(document);

        this.mutationObserver = new MutationObserver((mutations) => {
            for (const mutation of mutations) {
                mutation.addedNodes.forEach((node) => {
                    if (node instanceof Element) {
                        if (node.matches(this.trackable.selector)) {
                            observeElement(node);
                        }
                        scanAndObserve(node);
                    }
                });

                mutation.removedNodes.forEach((node) => {
                    if (node instanceof Element) {
                        closeSpansForSubtree(
                            node,
                            this.cache,
                            this.trackable,
                            this.clock
                        );
                        // Remove from the observed set so the element can be
                        // re-observed if it is re-inserted into the DOM later.
                        this.observedElements?.delete(node);
                        node.querySelectorAll(this.trackable.selector).forEach(
                            (desc) => {
                                this.observedElements?.delete(desc);
                            }
                        );
                    }
                });

                if (
                    mutation.type === "attributes" &&
                    mutation.target instanceof Element
                ) {
                    const el = mutation.target;
                    if (!isElementVisuallyPresent(el)) {
                        // Close the target element itself (if tracked).
                        const elementKey = this.trackable.getKey(el);
                        if (elementKey) {
                            endElementDwellSpan(
                                this.cache,
                                elementKey,
                                this.clock()
                            );
                        }
                        // Also close any tracked descendants that are now
                        // hidden by this ancestor's attribute change (e.g. a
                        // parent gains class="hidden" or style="display:none").
                        el.querySelectorAll(this.trackable.selector).forEach(
                            (desc) => {
                                const key = this.trackable.getKey(desc);
                                if (key) {
                                    endElementDwellSpan(
                                        this.cache,
                                        key,
                                        this.clock()
                                    );
                                }
                            }
                        );
                    }
                }
            }
        });

        this.mutationObserver.observe(document.body, {
            childList: true,
            subtree: true,
            attributes: true,
            attributeFilter: ["style", "class", "hidden"]
        });
    };

    public stop = () => {
        if (!this.started) {
            return;
        }
        this.started = false;
        this.observersByThreshold?.forEach((observer) => observer.disconnect());
        this.observersByThreshold?.clear();
        this.observersByThreshold = undefined;
        this.mutationObserver?.disconnect();
        this.mutationObserver = undefined;
        this.observedElements = undefined;
    };
}

// Resolve `trackable.intersectionThreshold` (number or per-element function)
// for `el`, clamped to [0, 1]. Falls back to `FALLBACK_INTERSECTION_THRESHOLD`
// for non-finite values.
function resolveThreshold(trackable: ElementTrackability, el: Element): number {
    const raw =
        typeof trackable.intersectionThreshold === "function"
            ? trackable.intersectionThreshold(el)
            : trackable.intersectionThreshold;
    if (!Number.isFinite(raw)) {
        return FALLBACK_INTERSECTION_THRESHOLD;
    }
    return Math.max(0, Math.min(1, raw));
}

// Return true only if the element is connected to the DOM and not hidden
// via `display: none` or `visibility: hidden` (we treat both as out-of-view).
function isElementVisuallyPresent(el: Element): boolean {
    if (!el.isConnected) {
        return false;
    }
    if (
        typeof window === "undefined" ||
        typeof window.getComputedStyle !== "function"
    ) {
        return true;
    }
    const style = window.getComputedStyle(el);
    if (style.visibility === "hidden" || style.display === "none") {
        return false;
    }
    return true;
}

// For every trackable element in (and including) `root`, close its active
// dwell span at the current time. Used when DOM nodes are removed so
// their open spans don't bleed past their lifetime.
function closeSpansForSubtree(
    root: Element,
    cache: ElementDwellSpansCache,
    trackable: ElementTrackability,
    clock: Clock
): void {
    if (root.matches(trackable.selector)) {
        const key = trackable.getKey(root);
        if (key) {
            endElementDwellSpan(cache, key, clock());
        }
    }
    root.querySelectorAll(trackable.selector).forEach((descendant) => {
        const key = trackable.getKey(descendant);
        if (key) {
            endElementDwellSpan(cache, key, clock());
        }
    });
}
